import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, numberAttribute, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router} from '@angular/router';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import { NetworkMigrationService } from '@services/network-migration.service';
import { NotificationBlockComponent, StatusClass } from '@components/notification-block/notification-block.component';
import {
  NETWORK_MIGRATION_STATUS_LABELS,
  NetworkMigrationStatus} from '@app/shared/models/network-migration.model';
import { EMPTY, exhaustMap, Subscription, takeWhile, timer } from 'rxjs';
import {
  BLEKeyDeliveryProgressApiResponse,
  BLEKeyDeliveryProgressStepApiResponse,
  BLEKeyDeliveryStatus,
  BLEKeyDeliveryStepStatus
} from '@app/shared/models/nms.models';
import { catchError } from 'rxjs/operators';
import { formatDistanceToNow } from 'date-fns';

type MigrationProgressTone = 'info' | 'success' | 'fail';

interface MigrationWorkflowStep {
  id: number;
  title: string;
  status: NetworkMigrationStatus;
  detail?: string;
  attempts?: BLEKeyDeliveryProgressStepApiResponse[];
}

@Component({
  selector: 'app-migration-workflow',
  imports: [MatButtonModule, MatCardModule, MatExpansionModule, MatProgressSpinnerModule, NotificationBlockComponent],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './migration-workflow.component.html',
  styleUrl: './migration-workflow.component.scss'
})
export class MigrationWorkflowComponent implements OnInit {
  private readonly POLLING_INTERVAL = 10000;
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly api = inject(NetworkMigrationService);
  private progressPollingSubscription?: Subscription;
  protected readonly StatusClass = StatusClass;

  readonly buildingId = input(0, { transform: numberAttribute });
  readonly migrationId = input(null, { transform: numberAttribute });
  readonly workflowBuildingName = input('');
  readonly workflowBuildingCode = input('');
  readonly workflowCustomer = input('');
  readonly workflowStatus = input<NetworkMigrationStatus | ''>('');

  readonly loading = signal(true);
  readonly contextError = signal('');
  readonly bleKeyDeliveryError = signal('');
  readonly BLEKeyDeliveryStepStatus = BLEKeyDeliveryStepStatus;
  readonly BLEKeyDeliveryStatus = BLEKeyDeliveryStatus;

  readonly bleKeyDeliveryResponse = signal<BLEKeyDeliveryProgressApiResponse>({
    steps: [],
    status: BLEKeyDeliveryStatus.NoOpenJoiningSession,
    openJoiningSessionId: 0,
    message: '',
    totalNodes: 0,
    completedNodes: 0,
    failedNodes: 0,
    workflowId: 0,
    updatedAt: ''
  });
  readonly retries = computed(() => Math.max(0, this.bleKeyDeliveryResponse().steps.length - 1));
  readonly retriesLabel = computed(() => {
    const retries = this.retries();
    if (retries === 0) {
      return '';
    }
    return `${retries} ${retries === 1 ? 'retry' : 'retries'}`;
  });

  readonly allowRetry = computed(() => this.bleKeyDeliveryResponse().status === BLEKeyDeliveryStatus.ActionRequired);
  readonly migrationCompletion = computed(() => {
    const totalNodes = this.bleKeyDeliveryResponse().totalNodes;
    return totalNodes ? Math.round(((this.bleKeyDeliveryResponse().completedNodes ?? 0) / totalNodes) * 100) : 0;
  });
  readonly totalNodes = computed(() => this.bleKeyDeliveryResponse().totalNodes ?? 0);
  readonly migratedNodes = computed(() => this.bleKeyDeliveryResponse().completedNodes ?? 0);
  readonly failedNodes = computed(() => this.bleKeyDeliveryResponse().failedNodes ?? 0);
  readonly pendingNodes = computed(() => Math.max(0, this.totalNodes() - this.migratedNodes() - this.failedNodes()));
  readonly progressTone = computed<MigrationProgressTone>(() => {
    const response = this.bleKeyDeliveryResponse();

    if (response.status === BLEKeyDeliveryStatus.ActionRequired || this.failedNodes() > 0) {
      return 'fail';
    }

    if (response.status === BLEKeyDeliveryStatus.Completed || this.migrationCompletion() === 100) {
      return 'success';
    }

    return 'info';
  });

  readonly workflowSteps = computed<MigrationWorkflowStep[]>(() => {
    const steps = this.bleKeyDeliveryResponse().steps;
    const status = this.bleKeyDeliveryResponse().status;
    return [
      {
        id: 1,
        title: 'BLE Key Delivery',
        status,
        attempts: steps
      }
    ];
  });
  readonly workflowCompletedSteps = computed(
    () => this.workflowSteps().filter((step) => step.status === BLEKeyDeliveryStatus.Completed).length
  );
  readonly workflowTotalSteps = 1; //computed(() => this.workflowSteps().length);
  readonly expandedWorkflowStepIds = signal<Set<number>>(new Set([1]));
  readonly areAllWorkflowStepsExpanded = computed(() => {
    const workflowSteps = this.workflowSteps();
    const expandedStepIds = this.expandedWorkflowStepIds();

    return workflowSteps.length > 0 && workflowSteps.every((step) => expandedStepIds.has(step.id));
  });

  readonly workflowExpansionActionLabel = computed(() =>
    this.areAllWorkflowStepsExpanded() ? 'Collapse all' : 'Expand all'
  );

  readonly bannerBuildingName = computed(
    () => this.workflowBuildingName() || `#${this.buildingId()}`);
  readonly bannerUpdatedAt = computed(() => {
    const updatedAt = new Date(this.bleKeyDeliveryResponse().updatedAt);
    return Number.isNaN(updatedAt.getTime()) ? '—' : `${formatDistanceToNow(updatedAt, { addSuffix: true })}`;
  });
  readonly bannerStatus = computed<NetworkMigrationStatus>(() => this.bleKeyDeliveryResponse().status || 'PENDING');
  readonly bannerStatusLabel = computed(() => NETWORK_MIGRATION_STATUS_LABELS[this.bannerStatus()]);

  ngOnInit(): void {
    this.loadContext();
  }

  setWorkflowStepExpanded(stepId: number, expanded: boolean): void {
    const next = new Set(this.expandedWorkflowStepIds());
    if (expanded) {
      next.add(stepId);
    } else {
      next.delete(stepId);
    }
    this.expandedWorkflowStepIds.set(next);
  }

  toggleWorkflowSteps(): void {
    if (this.areAllWorkflowStepsExpanded()) {
      this.expandedWorkflowStepIds.set(new Set());
      return;
    }

    this.expandedWorkflowStepIds.set(new Set(this.workflowSteps().map((step) => step.id)));
  }

  goBackToNetworkMigration(): void {
    void this.router.navigate(['/global-administration', 'network-migration']);
  }

  loadContext(): void {
    this.loading.set(true);
    this.contextError.set('');
    this.bleKeyDeliveryError.set('');
    this.stopProgressPolling();
    const buildingId = this.buildingId();
    const migrationId = this.migrationId();

    if (
      !Number.isSafeInteger(buildingId) ||
      buildingId <= 0 ||
      !Number.isSafeInteger(migrationId) ||
      migrationId <= 0
    ) {
      this.contextError.set('Invalid building or migration ID.');
      this.loading.set(false);
      return;
    }

    this.startProgressPolling(buildingId, migrationId);
  }

  retryBleKeyDelivery(): void {
    if (!this.allowRetry() || this.migrationId() === undefined) {
      return;
    }

    this.loading.set(true);
    this.api
      .retryBLEKeyDelivery(this.buildingId(), this.migrationId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.startProgressPolling(this.buildingId(), this.migrationId()),
        error: () => {
          this.bleKeyDeliveryError.set('Unable to retry BLE key delivery.');
          this.loading.set(false);
        }
      });
  }

  private startProgressPolling(buildingId: number, migrationId: number): void {
    this.stopProgressPolling();
    this.bleKeyDeliveryError.set('');
    this.progressPollingSubscription = timer(0, this.POLLING_INTERVAL)
      .pipe(
        exhaustMap(() =>
          this.api.getBuildingMigrationTransferProgress(buildingId, migrationId).pipe(
            catchError(() => {
              this.bleKeyDeliveryError.set('Unable to load BLE key delivery progress.');
              this.loading.set(false);

              return EMPTY;
            })
          )
        ),
        takeWhile(
          (response) =>
            response.status !== BLEKeyDeliveryStatus.Completed &&
            response.status !== BLEKeyDeliveryStatus.ActionRequired &&
            response.status !== BLEKeyDeliveryStatus.Failed &&
            response.status !== BLEKeyDeliveryStatus.Cancelled,
          true
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((bleKeyDeliveryResponse) => {
        this.bleKeyDeliveryResponse.set(bleKeyDeliveryResponse);
        this.bleKeyDeliveryError.set('');
        this.loading.set(false);
      });
  }

  private stopProgressPolling(): void {
    this.progressPollingSubscription?.unsubscribe();
    this.progressPollingSubscription = undefined;
  }
}
